import { useQuery } from '@tanstack/react-query';

import type { GetMarkPickupFulfilledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get pickup fulfilled status from a purchased vehicle
 */
export const getMarkPickupFulfilled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkPickupFulfilledResponse> => {
	const response = await apiClient.get<GetMarkPickupFulfilledResponse>(
		ordersEndpoints.markPickupFulfilled(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting pickup fulfilled status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkPickupFulfilled = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markPickupFulfilled.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get pickup fulfilled status without an order ID.');
			}
			return getMarkPickupFulfilled(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
